import { UIMatch } from 'react-router-dom';

const MAX_LENGTH = 300;
const SPLAT = '*';

const decode = (segment: string) => {
    try {
        return decodeURIComponent(segment);
    } catch {
        return segment;
    }
};

/**
 * The route the visitor is on as its pattern ("/incidents/reports/:caseId"),
 * never as the address with its real values. Some routes carry identifiers
 * (a case ID is half of a guest reporter's credentials), and support gets this
 * together with the visitor's email, so no parameter value may reach it.
 *
 * Returns `undefined` when no route matched, when only the catch-all matched
 * (an unknown address is whatever the visitor typed), and when a value cannot
 * be told apart from a fixed word of the route. It never falls back to the
 * raw address.
 */
export const getRoutePattern = (
    matches: Pick<UIMatch, 'params' | 'pathname'>[],
): string | undefined => {
    const deepest = matches[matches.length - 1];
    if (!deepest) return undefined;

    const { [SPLAT]: splat, ...named } = deepest.params;
    // Parameters come in the order they appear in the route.
    const values = Object.entries(named).filter(
        (entry): entry is [string, string] => entry[1] !== undefined,
    );

    const segments = deepest.pathname.split('/').filter(Boolean);
    if (splat !== undefined) {
        const splatSegments = splat ? splat.split('/').length : 0;
        segments.splice(Math.max(0, segments.length - splatSegments));
    }

    let next = 0;
    const pattern = segments.map((segment) => {
        const [name, value] = values[next] ?? [];
        if (name === undefined || decode(segment) !== value) return segment;
        next += 1;
        return `:${name}`;
    });
    if (splat !== undefined) pattern.push(SPLAT);

    const everyValueNamed = next === values.length;
    const valueLeftOver = pattern.some((segment) =>
        values.some(([, value]) => decode(segment) === value),
    );
    const onlyCatchAll = pattern.length === 1 && pattern[0] === SPLAT;
    if (!everyValueNamed || valueLeftOver || onlyCatchAll) return undefined;

    return `/${pattern.join('/')}`.slice(0, MAX_LENGTH);
};
